import { useContext, useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  CreditCard,
  ShoppingBag,
  Lock,
  CheckCircle,
  ErrorOutline,
  SentimentDissatisfied,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import { useHistory, Link } from "react-router-dom";
import AuthContext from "../../Context/auth/AuthContext";
import MethodGet, { MethodPost } from "../../config/Service";
import OpenPayForm from "../../components/Checkout/CheckoutOrder/OpenPay/OpenPayForm";
import EmailVerification from "../../components/Checkout/EmailVerification";
import { PublicLayout } from "../../components/Layout/PublicLayout";
import CurrencyFormat from "../../utils/FormatCurrency";

const breadcrumbsData = [
  { label: "Inicio", path: "/" },
  { label: "Mis Pedidos", path: "/pedidos" },
  { label: "Completar Pago", path: "#" },
];

const MC = {
  primary: "#801F56",
  accent: "#E46EA7",
  accentSoft: "#F3CDD4",
  blushLight: "#FFF0F5",
  white: "#FFFFFF",
  dark: "#2D1B30",
  grey: "#6B6B6B",
  success: "#4CAF50",
  error: "#E53935",
  warning: "#FF9800",
};

const PayOrderPage = (props) => {
  const { id } = props.match.params;
  const { autenticado } = useContext(AuthContext);
  const history = useHistory();

  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [paying, setPaying] = useState(false);

  const openPayRef = useRef(null);

  // ─── Cargar detalles del pedido ───────────────────────────────────────
  useEffect(() => {
    if (!autenticado) return;

    const fetchOrder = async () => {
      try {
        const res = await MethodGet(`/distribucion/Sales/${id}`);
        const data = res.data;

        // show() returns { venta: sale }
        let venta = null;
        if (data && data.venta) {
          venta = data.venta;
        } else if (Array.isArray(data) && data.length > 0) {
          venta = data[0];
        } else if (data && data.id) {
          venta = data;
        }

        if (!venta || !venta.id) {
          setError("Pedido no encontrado.");
          setErrorType("not_found");
          setLoading(false);
          return;
        }

        if (venta.status !== 0) {
          setError(
            venta.status === 4
              ? "Este pedido fue cancelado."
              : "Este pedido ya fue procesado."
          );
          setErrorType("already_paid");
          setLoading(false);
          return;
        }

        if (!["openpay", "tarjeta"].includes(venta.payment_type)) {
          setError(
            "Este pedido fue creado con transferencia bancaria. Ve a tu perfil de pedidos para ver las instrucciones."
          );
          setErrorType("not_card");
          setLoading(false);
          return;
        }

        if (venta.payment_status === "completed") {
          setError("Este pedido ya fue pagado exitosamente.");
          setErrorType("already_paid");
          setLoading(false);
          return;
        }

        setOrder(venta);
        setProducts(venta.details || []);
        setLoading(false);
      } catch (err) {
        console.error("PayOrderPage error:", err.response?.status, err.response?.data || err.message);
        if (err.response?.status === 401) {
          setError("Tu sesión expiró. Recarga la página e inicia sesión de nuevo.");
        } else if (err.response?.status === 403) {
          setError("No tienes permiso para ver este pedido.");
        } else if (err.response?.status === 500) {
          setError("Error del servidor. Intenta recargar la página.");
        } else {
          setError("Pedido no encontrado.");
        }
        setErrorType("not_found");
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, autenticado]);

  // ─── Pagar ────────────────────────────────────────────────────────────
  const handlePay = async () => {
    if (!openPayRef.current) {
      Swal.fire({ title: "Error", text: "Formulario no disponible. Recarga la página.", icon: "error" });
      return;
    }

    setPaying(true);

    try {
      const tokenData = await openPayRef.current.tokenize();

      Swal.fire({
        title: "Procesando pago...",
        text: "No cierres esta ventana.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const response = await MethodPost(`/distribucion/Sales/${id}/pay-card`, {
        token: tokenData.token,
        device_session_id: tokenData.device_session_id,
      });

      if (response?.data?.needs_3ds && response?.data?.redirect_url) {
        Swal.fire({
          title: "Verificación bancaria",
          text: "Serás redirigido a tu banco...",
          icon: "info",
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          window.location.href = response.data.redirect_url;
        });
        return;
      }

      Swal.fire({
        title: "¡Pago exitoso! 🎉",
        text: "Tu pago ha sido procesado correctamente.",
        icon: "success",
        timer: 3500,
        showConfirmButton: false,
      }).then(() => {
        history.push("/pedidos");
      });
    } catch (err) {
      setPaying(false);
      const errorData = err.response?.data;
      const errorMsg = errorData?.openpay_error
        ? errorData.error || "Tu tarjeta fue rechazada."
        : errorData?.error || "Ocurrió un error al procesar el pago.";

      Swal.fire({
        title: errorData?.openpay_error ? "Pago rechazado" : "Error",
        text: errorMsg,
        icon: "error",
        showConfirmButton: true,
      });
    }
  };

  // ─── CARGANDO ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <PublicLayout breadcrumb={breadcrumbsData} breadcrumbTitle="Completar Pago">
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress sx={{ color: MC.primary }} />
        </Box>
      </PublicLayout>
    );
  }

  // ─── ERROR ────────────────────────────────────────────────────────────
  if (error) {
    const isNotFound = errorType === "not_found";
    return (
      <PublicLayout breadcrumb={breadcrumbsData} breadcrumbTitle="Completar Pago">
        <Box sx={{ maxWidth: 500, mx: "auto", px: 2, py: 8, textAlign: "center" }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 4,
              border: `2px solid ${isNotFound ? "#FFCDD2" : MC.accentSoft}`,
              background: isNotFound
                ? "linear-gradient(145deg, #FFF5F5 0%, #FFFFFF 100%)"
                : `linear-gradient(145deg, ${MC.blushLight} 0%, ${MC.white} 100%)`,
            }}
          >
            {isNotFound ? (
              <SentimentDissatisfied sx={{ fontSize: 64, color: MC.error, mb: 2 }} />
            ) : (
              <ErrorOutline sx={{ fontSize: 64, color: MC.warning, mb: 2 }} />
            )}
            <Typography variant="h5" fontWeight={700} color={MC.dark} gutterBottom>
              {isNotFound ? "Pedido no encontrado" : "No se puede pagar"}
            </Typography>
            <Typography variant="body1" color={MC.grey} sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button
              component={Link}
              to="/pedidos"
              variant="contained"
              sx={{
                py: 1.2, px: 4, borderRadius: 3, fontWeight: 600, textTransform: "none",
                background: `linear-gradient(135deg, ${MC.primary} 0%, ${MC.accent} 100%)`,
                "&:hover": { background: `linear-gradient(135deg, #6B1A48 0%, #D87390 100%)` },
              }}
            >
              Ver mis pedidos
            </Button>
          </Paper>
        </Box>
      </PublicLayout>
    );
  }

  // ─── FORMULARIO DE PAGO ───────────────────────────────────────────────
  if (!order) return null;

  return (
    <PublicLayout breadcrumb={breadcrumbsData} breadcrumbTitle={`Completar Pago — Pedido #${order.id}`}>
      <Box sx={{ maxWidth: 700, mx: "auto", px: 2, py: 4 }}>
        {/* Resumen del pedido */}
        <Paper
          elevation={0}
          sx={{
            p: 3, mb: 3, borderRadius: 3,
            border: `2px solid ${MC.accentSoft}`,
            background: `linear-gradient(145deg, ${MC.blushLight} 0%, ${MC.white} 100%)`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <ShoppingBag sx={{ color: MC.primary }} />
            <Typography variant="h6" fontWeight={700} color={MC.dark}>
              Pedido #{order.id}
            </Typography>
            <Chip
              label="Pendiente de pago"
              size="small"
              sx={{ ml: "auto", background: "#FFF3E0", color: "#E65100", fontWeight: 600 }}
            />
          </Box>
          <Divider sx={{ mb: 2, borderColor: MC.accentSoft }} />

          {products.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {products.map((detail, idx) => (
                <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", py: 0.8 }}>
                  <Typography variant="body2" color={MC.dark}>
                    {detail.product?.name || `Producto #${detail.id_product}`} × {detail.quantity}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color={MC.dark}>
                    {CurrencyFormat(detail.final_price * detail.quantity)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Typography variant="body1" color={MC.grey}>Costo de envío</Typography>
            <Typography variant="body1" fontWeight={600} color={MC.dark}>
              {CurrencyFormat(Number(order.shipping_cost) || 0)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight={700} color={MC.primary}>Total a pagar</Typography>
            <Typography variant="h5" fontWeight={800} color={MC.primary}>
              {CurrencyFormat(order.total + (Number(order.shipping_cost) || 0))}
            </Typography>
          </Box>
        </Paper>

        {/* Formulario OpenPay */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: `2px solid ${MC.accentSoft}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CreditCard sx={{ color: MC.primary }} />
            <Typography variant="h6" fontWeight={700} color={MC.dark}>Datos de tu tarjeta</Typography>
            <Chip
              label="3D Secure"
              size="small"
              sx={{
                ml: "auto",
                background: `linear-gradient(135deg, ${MC.primary}, ${MC.accent})`,
                color: MC.white, fontWeight: 700, fontSize: "0.65rem",
              }}
            />
          </Box>
          <EmailVerification />
          <Box sx={{ mt: 2 }}>
            <OpenPayForm ref={openPayRef} />
          </Box>
        </Paper>

        {/* Botón de pago */}
        <Button
          fullWidth variant="contained" disabled={paying} onClick={handlePay}
          sx={{
            py: 1.8, borderRadius: 3, fontWeight: 700, fontSize: "1.1rem", textTransform: "none",
            background: `linear-gradient(135deg, ${MC.primary} 0%, ${MC.accent} 100%)`,
            boxShadow: "0 6px 24px rgba(128, 31, 86, 0.35)",
            "&:hover": { background: `linear-gradient(135deg, #6B1A48 0%, #D87390 100%)` },
            "&:disabled": { background: "#e0e0e0", color: "#9e9e9e" },
            transition: "all 0.3s ease",
          }}
        >
          {paying ? "⏳ Procesando pago..." : `💳 Pagar ahora`}
        </Button>

        <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
          <Lock sx={{ color: MC.success, fontSize: 16 }} />
          <Typography variant="caption" sx={{ color: MC.grey, fontWeight: 500 }}>
            Pago seguro procesado por OpenPay con cifrado SSL
          </Typography>
          <CheckCircle sx={{ color: MC.success, fontSize: 14 }} />
        </Box>
      </Box>
    </PublicLayout>
  );
};

export default PayOrderPage;
